import { Outlet, useMatches } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { AnimatePresence } from 'motion/react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { WebSidebarToggle } from '@/components/blocks/Layout/WebSidebarToggle';
import { BrowserStreamPanels } from '@/features/browser-stream/BrowserStreamPanels';
import { chatDebugStore, ChatDebugLayout } from '@/features/chat-debug';
import { ContextChatSidebar } from '@/features/context-chat';
import { LocalTerminalPanel } from '@/features/sprites/LocalTerminalPanel';
import { useRegisterToggleTerminalHotkey } from '@/hooks/hotkeys/useRegisterToggleTerminalHotkey';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { useTypeScaleHotkeys } from '@/hooks/useTypeScaleHotkeys';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';
import { inElectron } from '@/utils';

import { useWindowTitle } from './Header/hooks/useWindowTitle';
import { PanelHeader } from './Header/PanelHeader';
import { MainLayoutPanelTunnel } from './main-layout-tunnel';
import { mainLayoutStore } from './main-layout.store';

const MainLayout = observer(function MainLayout() {
  useWindowTitle();
  useTypeScaleHotkeys();
  useRegisterToggleTerminalHotkey();

  // Keep --main-outlet-height in sync with the outlet's actual rendered height
  // so child components (Thread, chat-new) and --chat-thread can size themselves
  // correctly regardless of what sits above the outlet (banner, future elements).
  const outletRef = useRef<HTMLDivElement>(null);
  const isTranslucentPane = useMatches({
    select: (matches) =>
      matches[matches.length - 1]?.staticData.paneSurface === 'translucent',
  });
  const isHeaderOverlay = useMatches({
    select: (matches) =>
      matches[matches.length - 1]?.staticData.headerOverlay === true,
  });
  useLayoutEffect(() => {
    const el = outletRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty(
        '--main-outlet-height',
        `${el.offsetHeight}px`
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const [contentRef, contentRect] = useResizeObserver<HTMLDivElement>();
  useEffect(() => {
    mainLayoutStore.setContentWidth(contentRect.width);
  }, [contentRect.width]);

  return (
    <div
      className={cn(
        'relative flex h-full',
        isTranslucentPane ? 'shell-glow' : 'bg-background-subtle'
      )}
    >
      <style>
        {`
          :root {
            --titlebar-height: 0rem;
            --tab-bar-height: 0rem;
            --panel-header-height: 4rem;
            --chat-message-box-container-height: 7.8125rem;
          }`}
      </style>
      <div ref={contentRef} className="relative flex h-full min-w-0 flex-1">
        <div className="relative flex min-w-[300px] flex-1 flex-col overflow-hidden">
          {isHeaderOverlay ? (
            <div className="absolute inset-x-0 top-0 z-10">
              <PanelHeader />
            </div>
          ) : (
            <PanelHeader />
          )}
          {/* chromeless surfaces (agent threads, agents pages) skip the panel
          header entirely; on web they still need the sidebar toggle */}
          {uiStore.chromeless && !inElectron() && (
            <div className="absolute left-2 top-2 z-20 rounded-lg bg-surface p-1 shadow-sm">
              <WebSidebarToggle />
            </div>
          )}
          <div
            ref={outletRef}
            className="relative flex overflow-hidden flex-1 min-h-0"
          >
            <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
              <Outlet />
            </div>
            <ContextChatSidebar />
          </div>
          {/* App-wide local terminal dock: overlays the bottom of the main column on every route */}
          <LocalTerminalPanel />
        </div>
        <BrowserStreamPanels />
        <MainLayoutPanelTunnel.Out />
      </div>
      <AnimatePresence>
        {chatDebugStore.active?.isOpen && <ChatDebugLayout />}
      </AnimatePresence>
    </div>
  );
});

export default MainLayout;
